import {
  FORMAT_DATE_DEFAULT,
  FORMAT_TIME_DEFAULT,
  TimeFunction,
} from "@/common/datetime/TimeFunction";
import { IMeetingInfo } from "@/common/entity/Entity";
import { BuildRCAttribute } from "@/common/function/RCAttribute";
import {
  RenderIconCalendar,
  RenderIconInfo,
} from "@/components/ui/Icons/IconsCustom";
import Nodata from "@/components/ui/Nodata";
import ResourceItem from "@/components/ui/ResourceItem";
import TimeLine from "@/components/ui/TimeLine";
import { buildEventList } from "@/components/ui/TimeLine/constains/FunctionTimeline";
import { TimeRangeSliderCanvas } from "@/components/ui/TimeRangeSlider";
import { RootState } from "@/redux";
import { IOrganizationStore } from "@/redux/slices/organizations/OrganizationModel";
import { IResourceStore } from "@/redux/slices/resources/ResourceReducerModel";
import { ISettingStore } from "@/redux/slices/settings/SettingModel";
import { TransitiveMemberOfGroup } from "@/services";
import { DateTimePeriod } from "@/services/organization-service/entity/OutLookEntity";
import { ELoadMorePagingDefault } from "@/services/resource-service/entity/Enums";
import {
  AppointmentByTimeRangeInfo,
  IFetchResourceRequest,
  IFilterResourceRequest,
  IResourceRequestBody,
  ResourceInfo,
} from "@/services/resource-service/entity/ResourceEntity";
import Availability from "@/view/addin/components/availability";
import IconPermission from "@/view/addin/components/icon-permission";
import TentativePermission from "@/view/addin/components/icon-permission/TentativeIcon";
import ResourceCalendar from "@/view/addin/components/resource-calendar";
import { IRefComponentType } from "@/view/addin/components/resource-calendar/Models";
import ResourceDetail from "@/view/addin/components/resource-detail";
import { IRefComponentDetailType } from "@/view/addin/components/resource-detail/Models";
import { Spinner } from "@fluentui/react-components";
import { useInfiniteQuery } from "@tanstack/react-query";
import { cloneDeep } from "lodash";
import { RefObject, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { IResourceListProps } from "./Models";
import {
  BaseListResourceWrapper,
  LoadMoreWrapper,
  ResourceInfoWrapper,
  StickyElementWrapper,
} from "./Styles";
import ScrollDismissTooltip from "@/components/ui/Tooltip";
import { ITimeLineEvent } from "@/components/ui/TimeLine/Models";

const ResourceList = (props: IResourceListProps) => {
  const { t } = useTranslation();
  const refCalendar: RefObject<IRefComponentType | null> = useRef(null);
  const refDetail: RefObject<IRefComponentDetailType | null> = useRef(null);
  const resourceStore: IResourceStore = useSelector(
    (state: RootState) => state.resources
  ) as IResourceStore;
  const resourceSelected: ResourceInfo[] | null =
    resourceStore.selectedResource;

  const orgStore: IOrganizationStore = useSelector(
    (state: RootState) => state.orgInfo
  ) as IOrganizationStore;
  const settingStore: ISettingStore = useSelector(
    (state: RootState) => state.settingInfo
  ) as ISettingStore;
  const dateFormat = settingStore?.dateTimeFormat.dateTimeSeparator;
  const timeFormat = settingStore?.dateTimeFormat.timeFormat;
  const firstDayOfWeek = Number(settingStore?.dateTimeFormat.firstDayOfWeek || "0");

  const [timeSlider, setTimeSlider] = useState(
    orgStore.outlookInfo?.displayStartTime || new Date()
  );
  const [eventNextDate, setEventNextDate] = useState<
    Record<string, { startDate: string[]; events: ITimeLineEvent[] }>
  >({});
  const [resourceRequest, setResourceRequest] =
    useState<IFetchResourceRequest | null>(null);
  const [total, setTotal] = useState<number>(0);
  //const isMounted: MutableRefObject<boolean> = useRef(false);

  const { getResources } = props;

  useEffect(() => {
    if (orgStore.orgInfo && resourceStore?.filterResource) {
      const filterReq: IFilterResourceRequest = {
        NameContains: resourceStore?.filterResource?.name?.trim() || null,
        LocationIds: resourceStore.filterResource?.locationIds?.length
          ? resourceStore.filterResource.locationIds.join(" or ")
          : null,
        CategoryIds: resourceStore.filterResource?.categoryIds?.length
          ? resourceStore.filterResource.categoryIds.join(
            ` ${resourceStore.filterResource.categoryCondition || "or"} `
          )
          : null,
        IsAvailable: resourceStore.filterResource.isAvailable,
        ResourceTypes: resourceStore.filterResource.resourceTypes || null
      };
      setEventNextDate({});
      setTimeSlider(orgStore.outlookInfo?.displayStartTime || new Date());
      onFetchResource(filterReq);
    }
  }, [
    JSON.stringify(resourceStore.filterResource),
    orgStore.orgInfo,
    orgStore.outlookInfo,
  ]);

  useEffect(() => {
    setEventNextDate({});
    setTimeSlider(orgStore.outlookInfo?.displayStartTime || new Date());
  }, [orgStore.outlookInfo?.displayStartTime]);

  const fetchResourceService = async (
    resourceRequest: IFetchResourceRequest | null
  ): Promise<ResourceInfo[]> => {
    if (getResources && resourceRequest) {
      const response = await getResources(resourceRequest);
      if (total !== response.total) {
        setTotal(response.total);
      }
      //Tìm resouce trùng guid trong originalSelectedResources
      const originalSelectedResources = resourceStore.originalSelectedResources || [];
      const resources = response?.data.map(item => {
        const originalResource = originalSelectedResources.find(original => original.guid === item.guid);
        item.orders = originalResource?.orders || null;
        return item;
      });
      return resources;
    }
    return [];
  };

  const concatData = (data: ResourceInfo[][]): ResourceInfo[] => {
    let tempData: ResourceInfo[] = [];
    data.forEach((items: ResourceInfo[]) => {
      tempData = tempData.concat(items);
    });
    return tempData;
  };

  const resourceQuery = useInfiniteQuery({
    queryKey: ["resources", resourceRequest],
    queryFn: (options) => {
      let resourceRequestClone = cloneDeep(resourceRequest);
      if (options.pageParam) {
        if (resourceRequestClone?.requestBody?.Filters) {
          resourceRequestClone.requestBody.Filters.PageSize =
            ELoadMorePagingDefault.PageSize;
          resourceRequestClone.requestBody.Filters.Page = options.pageParam + 1;
        } else {
          resourceRequestClone = {
            ...resourceRequestClone,
            requestBody: {
              ...(resourceRequestClone?.requestBody || {}),
              Filters: {
                PageSize: ELoadMorePagingDefault.PageSize,
                Page: options.pageParam + 1,
              },
            } as IResourceRequestBody,
          } as IFetchResourceRequest;
        }
      }
      return fetchResourceService(resourceRequestClone);
    },
    initialPageParam: 0,
    getNextPageParam: (_lastPage, allPage, currentPage: number) => {
      return concatData(allPage).length >= total ? undefined : currentPage + 1;
    },
  });

  useEffect(() => {
    if (props.updateLoading) {
      props.updateLoading(
        resourceQuery.isLoading ||
        (resourceQuery.isFetching && !resourceQuery.isFetchingNextPage)
      );
    }
    if (props.onChangeTotalResourceList) {
      props.onChangeTotalResourceList({
        quantity: resources.length,
        isLastPage: !resourceQuery.hasNextPage,
      });
    }
  }, [JSON.stringify(resourceQuery), resourceSelected?.length]);

  const loadMoreResource = async (): Promise<void> => {
    setEventNextDate({});
    setTimeSlider(orgStore.outlookInfo?.displayStartTime || new Date());
    await resourceQuery.fetchNextPage();
  };

  const onFetchResource = (filters?: IFilterResourceRequest | null): void => {
    if (getResources) {
      if (orgStore.orgInfo && orgStore.outlookInfo && orgStore.organizer) {
        const meetingInfos: IMeetingInfo[] = orgStore.outlookInfo
          .isRecurringMaster
          ? (orgStore.outlookInfo?.occurrences || [])?.map(
            (item: DateTimePeriod) => {
              return {
                StartTime: item.start?.toISOString() || "",
                EndTime: item.end?.toISOString() || "",
                Guid: orgStore.orgInfo?.isExternal
                  ? orgStore.orgInfo?.uidOutlook || undefined
                  : orgStore.orgInfo?.iCalUId,
              };
            }
          )
          : [
            {
              StartTime: orgStore.outlookInfo.start?.toISOString() || "",
              EndTime: orgStore.outlookInfo.end?.toISOString() || "",
              Guid: orgStore.orgInfo?.isExternal
                ? orgStore.orgInfo?.uidOutlook || undefined
                : orgStore.orgInfo?.iCalUId,
            },
          ];

        const request: IFetchResourceRequest = {
          orgId: orgStore.orgInfo.guid,
          tenantId: orgStore.orgInfo.tenantId,
          requestBody: {
            Organizer: orgStore.organizer.email,
            IsRecurring: !!orgStore.outlookInfo.isRecurringMaster,
            EmailGroups: orgStore.orgInfo.memberOfs.map(
              (item: TransitiveMemberOfGroup) => item.mail
            ),
            MeetingInfos: meetingInfos,
            Filters: filters || null,
            IsExternal: orgStore.orgInfo.isExternal,
            SeriesMasterUId: orgStore.orgInfo?.isExternal
              ? orgStore.orgInfo?.uidOutlook
              : undefined,
          },
        };
        setResourceRequest(request);
      }
    }
  };

  const onOpenCalendar = (
    ev: React.MouseEvent<HTMLImageElement>,
    resourceInfo: ResourceInfo
  ) => {
    ev.stopPropagation();
    if (refCalendar?.current?.onOpenResourceCalendar) {
      refCalendar?.current?.onOpenResourceCalendar(resourceInfo, orgStore.outlookInfo?.start || null);
    }
  };

  const onOpenResourceDetail = (
    ev: React.MouseEvent<HTMLImageElement>,
    resourceInfo: ResourceInfo
  ) => {
    ev.stopPropagation();
    if (refDetail?.current?.onOpenResourceDetail) {
      refDetail?.current?.onOpenResourceDetail(resourceInfo);
    }
  };

  const renderHeaderButtons = (resourceInfo: ResourceInfo): JSX.Element => {
    return (
      <>
        {resourceInfo.permission.meetingDeclines?.length ? (
          <IconPermission
            permission={resourceInfo.permission}
            exchangeProperty={resourceInfo.exchangeProperty}
            isRecurring={orgStore.outlookInfo?.isRecurringMaster}
            quantityMeetingInfo={orgStore.outlookInfo?.occurrences?.length}
          />
        ) : null}
        {resourceInfo.permission.isTentative ? (
          <TentativePermission
            permission={resourceInfo.permission}
            theme={settingStore.theme || undefined}
          />
        ) : null}
        <ScrollDismissTooltip content={t("Calendar")} relationship="label">
          <div
            onClick={(ev: React.MouseEvent<HTMLImageElement>) =>
              onOpenCalendar(ev, resourceInfo)
            }
            {...BuildRCAttribute(`resource_item.${resourceInfo.name}.calendar`)}
          >
            <RenderIconCalendar theme={settingStore.theme} />
          </div>
        </ScrollDismissTooltip>
        <ScrollDismissTooltip content={t("View Details")} relationship="label">
          <div
            onClick={(ev: React.MouseEvent<HTMLImageElement>) =>
              onOpenResourceDetail(ev, resourceInfo)
            }
            {...BuildRCAttribute(`resource_item.${resourceInfo.name}.detail`)}
          >
            <RenderIconInfo theme={settingStore.theme} />
          </div>
        </ScrollDismissTooltip>
        {/*  <Tooltip  content={t("Add to My Favorites")} relationship="label">
          <div
            {...BuildRCAttribute(
              `resource_item.${resourceInfo.name}.favorites`
            )}
          >
            <RenderIconStar theme={settingStore.theme} />
          </div>
        </Tooltip> */}
      </>
    );
  };

  const displayResources = (resourceList?: ResourceInfo[]): ResourceInfo[] => {
    const idResourceSelected: string[] = (resourceSelected || []).map(
      (resource: ResourceInfo) => resource.guid
    );
    return (resourceList || []).filter(
      (item: ResourceInfo) => !idResourceSelected.includes(item.guid)
    );
  };

  const resources = displayResources(
    concatData(resourceQuery.data?.pages || []) || []
  );

  const onSelectResource = async (resource: ResourceInfo): Promise<void> => {
    if (
      resources.length === 1 &&
      resources[0].email === resource.email &&
      resourceQuery.hasNextPage
    ) {
      await onLoadMoreResource();
    }
    if (props.selectResource) {
      props.selectResource(resource);
    }
  };

  const onLoadMoreResource = async (): Promise<void> => {
    await loadMoreResource();
  };

  const changeTime = async (timeSliderClone: Date) => {
    setTimeSlider(timeSliderClone);
    if(timeSlider.getDate() !== timeSliderClone.getDate()) {
      const currentDateClone = new Date(
        orgStore.outlookInfo?.displayStartTime || ""
      );
      const beforeCurrenDate = new Date(currentDateClone);
      beforeCurrenDate.setDate(currentDateClone.getDate() - 1);
      const afterCurrenDate = new Date(currentDateClone);
      afterCurrenDate.setDate(currentDateClone.getDate() + 1);
      const startDateNext: string = TimeFunction.dateTimeFormatter(timeSliderClone, FORMAT_DATE_DEFAULT) as string
  
      if (
        resources.length &&
        (!eventNextDate[resources[0].email]?.startDate?.length ||
          !eventNextDate[resources[0].email]?.startDate.includes(
            startDateNext
          )) &&
        (timeSliderClone <= beforeCurrenDate || timeSliderClone >= afterCurrenDate)
      ) {
        const resp = await onFetchAppointmentsByTimeRange(orgStore.orgInfo?.tenantId || "", timeSliderClone, resources.map(item => item.guid));
        const eventNextDateClone = cloneDeep(eventNextDate);
        resp.forEach(item => {
          if(!eventNextDateClone[item.resourceId] || (eventNextDateClone[item.resourceId] && !eventNextDateClone[item.resourceId].startDate.includes(startDateNext))) {
            const startDateClone = eventNextDateClone[item.resourceId]?.startDate || [];
            startDateClone.push(startDateNext)
            eventNextDateClone[item.resourceId] = {
              startDate: startDateClone,
              events: (eventNextDateClone[item.resourceId]?.events || []).concat(item.events || [])
            }
          }
        });
        setEventNextDate(eventNextDateClone);
      }
    }
  };

  const onFetchAppointmentsByTimeRange = async (
    tenantId: string,
    currentDate: Date,
    resourceIds: string[]
  ): Promise<AppointmentByTimeRangeInfo[]> => {
    if (props.onFetchAppointmentsByTimeRange) {
      const response = await props.onFetchAppointmentsByTimeRange(tenantId, currentDate, resourceIds);
      return response;
    }
    return [];
  }

  return (
    <>
      {resourceQuery.isLoading ||
        (resourceQuery.isFetching && !resourceQuery.isFetchingNextPage) ? (
        <Spinner style={{ marginTop: 15 }} />
      ) : resources.length ? (
        <>
          <BaseListResourceWrapper
            className="BaseListResourceWrapper"
            $maxHeight={
              (props.maxHeight || 300) + (resourceQuery.hasNextPage ? 0 : 25)
            }
          >
            {resources?.map((resource: ResourceInfo, index: number) => {
              return (
                <ResourceInfoWrapper
                  className="ResourceInfoWrapper"
                  $isAcceptSelect={
                    orgStore.outlookInfo?.isRecurringMaster
                      ? resource.permission.isAcceptSelectRecurring(
                        orgStore.outlookInfo?.occurrences?.length || 0
                      )
                      : resource.permission.isAcceptSelect
                  }
                  $isLastChild={index === resources.length - 1 &&
                    !orgStore.outlookInfo?.isRecurringMaster}
                  key={resource.guid}
                  onClick={() =>
                    (
                      orgStore.outlookInfo?.isRecurringMaster
                        ? resource.permission.isAcceptSelectRecurring(
                          orgStore.outlookInfo?.occurrences?.length || 0
                        )
                        : resource.permission.isAcceptSelect
                    )
                      ? onSelectResource(resource)
                      : undefined
                  }
                >
                  <ResourceItem
                    isSelected={false}
                    id={resource.guid}
                    avatar=""
                    name={`${resource.displayName || resource.name}`}
                    isShowClosingIcon={true}
                    model={resource}
                    leftBorderStyle={
                      orgStore.outlookInfo?.isRecurringMaster
                        ? resource.calculateBorderRecurring(
                          orgStore.outlookInfo?.occurrences?.length || 0
                        )
                        : resource.calculateBorder
                    }
                    isShowTimeLine={!orgStore.outlookInfo?.isRecurringMaster}
                    headerButtons={renderHeaderButtons(resource)}
                    theme={settingStore.theme || undefined}
                    availabilityBox={
                      <Availability
                        resource={resource}
                        meetingInfos={orgStore.outlookInfo?.occurrences}
                        dateFormat={settingStore?.dateTimeFormat?.dateTimeSeparator}
                      />
                    }
                    timeLineElements={
                      <TimeLine
                        timeSlider={timeSlider}
                        startTimeSelected={
                          orgStore.outlookInfo?.displayStartTime || new Date()
                        }
                        endTimeSelected={
                          orgStore.outlookInfo?.displayEndTime || new Date()
                        }
                        localizer="en-US"
                        isNoInformation={true}
                        dateTimeFormat={{
                          dateFormat: dateFormat || FORMAT_DATE_DEFAULT,
                          timeFormat: timeFormat || FORMAT_TIME_DEFAULT,
                        }}
                        events={buildEventList(timeSlider, resource, {
                          startTime: new Date(
                            orgStore.outlookInfo?.displayStartTime || ""
                          ),
                          endTime: new Date(
                            orgStore.outlookInfo?.displayEndTime || ""
                          ),
                        }).concat(eventNextDate[resource.guid]?.events || [])}
                        exchangePropertyResource={resource.exchangeProperty}
                      />
                    }
                  />
                </ResourceInfoWrapper>
              );
            })}
          </BaseListResourceWrapper>
          {!orgStore.outlookInfo?.isRecurringMaster ? (
            <StickyElementWrapper
              className="StickyElementWrapper"
              $theme={settingStore.theme || undefined}
            >
              <TimeRangeSliderCanvas
                key="resourceList"
                startTimeSelected={timeSlider}
                format={dateFormat || FORMAT_DATE_DEFAULT}
                formatHour={timeFormat || FORMAT_TIME_DEFAULT}
                onChange={changeTime}
                theme={settingStore.theme || undefined}
                rcName="resource_list"
              />
            </StickyElementWrapper>
          ) : null}
          {resourceQuery.hasNextPage ? (
            <LoadMoreWrapper
              className="LoadMoreWrapper"
              onClick={onLoadMoreResource}
            >
              {resourceQuery.isFetchingNextPage ? (
                <Spinner size="extra-tiny" />
              ) : (
                t("List more Resources")
              )}
            </LoadMoreWrapper>
          ) : null}
        </>
      ) : (
        <Nodata />
      )}
      <ResourceCalendar
        ref={refCalendar}
        dateFormat={dateFormat}
        timeFormat={timeFormat}
        firstDayOfWeek={firstDayOfWeek}
      />
      <ResourceDetail ref={refDetail} />
    </>
  );
};

export default ResourceList;
